import { Vector3, Euler, Matrix4 } from "three";
import { Arcs, ArcsOptions } from "../arcs/Arcs";

/**
 * Configuration for blockchain arc spawning
 */
export interface BlockchainArcsConfig {
  /** Size of each block (default: 400) */
  blockSize: number;
  /** Gap between blocks (default: 160) */
  blockGap: number;
  /** Number of blocks (default: 3) */
  numBlocks: number;
  /** Block movement speed in units/sec (default: 150) */
  moveSpeed: number;
  /** Z-axis rotation angle in radians (default: 0.785398 = 45 degrees) */
  zRotation: number;
  /** Maximum arcs at once (default: 2) */
  maxArcs: number;
  /** How often to spawn arcs [min, max] seconds (default: [0.8, 2.0]) */
  spawnInterval: [number, number];
  /** Arc lifetime [min, max] seconds (default: [0.3, 0.6]) */
  arcLifetime: [number, number];
}

const DEFAULT_BLOCKCHAIN_CONFIG: BlockchainArcsConfig = {
  blockSize: 400,
  blockGap: 160,
  numBlocks: 3,
  moveSpeed: 150,
  zRotation: 0.785398, // 45 degrees
  maxArcs: 3,
  spawnInterval: [1.0, 2.5],
  arcLifetime: [1.2, 2.4],
};

/**
 * Lightning arcs for the blockchain particle shape.
 * Arcs travel in the direction blocks are moving (left to right/center),
 * rotated to match the 45-degree Z rotation of the blocks.
 */
export class BlockchainArcs extends Arcs {
  private blockchainConfig: BlockchainArcsConfig;
  private lastSpawnTime = 0;
  private active = false;
  private transitionProgress = 0; // 0 = not visible, 1 = fully visible

  // Pre-computed values
  private stride: number;
  private halfPeriod: number;
  private rotationMatrix: Matrix4;

  constructor(
    blockchainConfig: Partial<BlockchainArcsConfig> = {},
    arcsOptions: ArcsOptions = {},
  ) {
    // Merge blockchain config
    const config = { ...DEFAULT_BLOCKCHAIN_CONFIG, ...blockchainConfig };

    // Configure the base Arcs class for blockchain style
    super({
      ...arcsOptions,
      config: {
        animation: {
          horizontalEasing: 0.15,
          verticalEasing: 0.08,
          targetChangeChance: 0.08,
          horizontalRange: 0.8,
          verticalRange: 0.5,
          jaggedness: 40, // Less jagged for cleaner look
          segmentsMin: 20,
          segmentsRange: 8,
          curveSegments: 30,
        },
        spawn: {
          maxArcs: config.maxArcs,
          spawnInterval: config.spawnInterval,
          lifetime: config.arcLifetime,
          settlementDelay: 0,
          sineSpeed: 0,
        },
        style: {
          glowLayers: [
            [0.15, 0.12, false],
            [0.1, 0.2, false],
            [0.05, 0.35, false],
            [0.025, 0.55, false],
            [0.01, 1.0, true],
          ],
          glowLayersMobile: [
            [0.08, 0.25, false],
            [0.04, 0.5, false],
            [0.015, 1.0, true],
          ],
          glowColor: { r: 0.48, g: 0.84, b: 0.94 }, // Blue
          coreColor: { r: 1.0, g: 1.0, b: 1.0 },
        },
        ...arcsOptions.config,
      },
    });

    this.blockchainConfig = config;

    // Pre-compute values
    this.stride = config.blockSize + config.blockGap;
    this.halfPeriod = (config.numBlocks * this.stride) / 2;

    // Create rotation matrix for the 45-degree Z rotation
    this.rotationMatrix = new Matrix4().makeRotationZ(config.zRotation);
  }

  /**
   * Set the transition progress (0-1) for fading in/out with shape transitions
   */
  public setTransitionProgress(progress: number): void {
    this.transitionProgress = Math.max(0, Math.min(1, progress));
    this.setIntensity(this.transitionProgress);
    this.active = progress > 0.1;
  }

  /**
   * Check if arcs are currently active
   */
  public isActive(): boolean {
    return this.active;
  }

  /**
   * Update the blockchain arcs
   * @param time Current time in seconds
   * @param deltaTime Time since last frame
   */
  public updateBlockchain(time: number, deltaTime: number = 1 / 60): void {
    if (!this.active) {
      this.clearArcs();
      return;
    }

    const { spawnInterval, arcLifetime } = this.blockchainConfig;

    // Spawn new arcs
    const timeSinceLastSpawn = time - this.lastSpawnTime;
    const [minInterval, maxInterval] = spawnInterval;
    const spawnChance =
      timeSinceLastSpawn > minInterval
        ? (timeSinceLastSpawn - minInterval) / (maxInterval - minInterval)
        : 0;

    if (
      this.getArcCount() < this.blockchainConfig.maxArcs &&
      Math.random() < spawnChance * deltaTime * 2
    ) {
      const { blockSize, blockGap, numBlocks } = this.blockchainConfig;

      // Calculate total layout dimensions
      // Block edges span from -halfTotalWidth to +halfTotalWidth
      const totalWidth = numBlocks * blockSize + (numBlocks - 1) * blockGap;
      const halfTotalWidth = totalWidth / 2;
      const halfBlockSize = blockSize / 2;

      // Arc spans from left edge to right edge of the block chain
      // Add small random inset so arc starts/ends slightly inside the blocks
      const inset = halfBlockSize * (0.1 + Math.random() * 0.2); // 10-30% inset from edges

      // Start at left edge (with small inset), end at right edge (with small inset)
      const startX = -halfTotalWidth + inset;
      const endX = halfTotalWidth - inset;

      // Keep Y and Z very close to center (dead center of blocks)
      // Only tiny variation to add slight visual interest
      const yOffset = (Math.random() - 0.5) * blockSize * 0.05;
      const zOffset = (Math.random() - 0.5) * blockSize * 0.05;

      const startPoint = new Vector3(startX, yOffset, zOffset);

      const endPoint = new Vector3(
        endX,
        yOffset + (Math.random() - 0.5) * blockSize * 0.05,
        zOffset + (Math.random() - 0.5) * blockSize * 0.05,
      );

      // Apply the 45-degree Z rotation to match block orientation
      startPoint.applyMatrix4(this.rotationMatrix);
      endPoint.applyMatrix4(this.rotationMatrix);

      const [minLife, maxLife] = arcLifetime;
      this.spawnArc({
        startPoint,
        endPoint,
        lifetime: minLife + Math.random() * (maxLife - minLife),
        intensity: 0.7 + Math.random() * 0.3,
      });

      this.lastSpawnTime = time;
    }

    // Update existing arcs
    this.update(deltaTime);
  }

  public dispose(): void {
    super.dispose();
  }
}
